import React, { useRef, useState } from "react";
import {
	IoHardwareChipOutline,
	IoSettingsOutline,
	IoSwapHorizontalOutline,
} from "react-icons/io5";
import { RiAlarmWarningLine } from "react-icons/ri";
import Card from "../../../components/Card/Card";
import List from "../../../components/List/List";
import Pagination from "../../../components/Pagination/Pagination";
import { useSSEContext } from "../../../contexts/sse";
import { useUserStore } from "../../../contexts/userStore";
import EventsService from "../../../services/EventsService";
import "./ListEvents.css";
import EventDetails from "../EventDetails/EventDetails";
import { getEventData, getEventTypeName } from "../../../constants/events";
import { formatDate, formatRelativeDate, isMoreThanOneDay } from "../../../utils/date";
import { TimeAgo } from "../../../components/TimeAgo/TimeAgo";
import { AllowAccessBadge } from "../../../components/AllowAccessBadge/AllowAccessBadge";
import { TbDoorEnter, TbDoorExit } from "react-icons/tb";
import { useTranslation } from "react-i18next";
import { DatetimeWithRelative } from "../../../components/DatetimeWithRelative/DatetimeWithRelative";

const DefineIcon = (props) => {
	if (props.tipoEvento === "Alarme") {
		return <RiAlarmWarningLine color="red" size={35} />;
	}
	if (props.tipoEvento === "Acesso") {
		return <IoSwapHorizontalOutline color="#0C86C6" size={35} />;
	}
	if (props.tipoEvento === "Configuracao") {
		return <IoSettingsOutline color="gray" size={35} />;
	}
	if (props.tipoEvento === "Sistema") {
		return <IoHardwareChipOutline color="gray" size={35} />;
	}
};

const ListEvents = ({ currentPage = 1, setCurrentPage, ...props }) => {
	const [events, setEvents] = useState([]);
	const [maxPage, setMaxPage] = useState(1);
	const [rowsPerPage, setRowsPerPage] = useState(12);
	const [total, setTotal] = useState(0);
	const { selectedCondominio } = useUserStore();
	const { sseReceived } = useSSEContext();
	const [collumnClicked, setCollumnClicked] = useState({
		collumnTitle: "id",
		orderDirection: "DESC",
	});
	const cancelTokenRef = useRef(null);
	const [isLoading, setIsLoading] = useState(true);
	const { t } = useTranslation();

	const getEvents = async (page, condominiumId, filters, collumnClicked) => {
		if (condominiumId === 0) return;
		if (cancelTokenRef.current) {
			cancelTokenRef.current.abort();
		}
		cancelTokenRef.current = new AbortController();
		const response = await EventsService.listCondominioEventsPaginated(
			page,
			condominiumId,
			filters,
			collumnClicked,
			false,
			rowsPerPage,
			cancelTokenRef.current.signal
		);
		setIsLoading(false);
		setEvents(response?.data?.results);
		setMaxPage(Math.ceil(response?.data?.total / rowsPerPage));
		setTotal(response?.data?.total);
	};

	React.useEffect(() => {
		getEvents(currentPage, selectedCondominio, props.filters, collumnClicked);
	}, [
		selectedCondominio,
		props.filters,
		collumnClicked,
		currentPage,
		rowsPerPage,
	]);

	React.useEffect(() => {
		if (currentPage !== 1) return;
		if (sseReceived?.action === "createEvent") {
			setEvents((oldEvents) => {
				const newEvents = [sseReceived.content, ...oldEvents];
				if (newEvents.length > rowsPerPage) {
					newEvents.pop();
				}
				return newEvents;
			});
		}
	}, [
		sseReceived,
	]);

	return (
		<React.Fragment>
			<div className="list-container list-visibility">
				<List
					isLoading={isLoading}
					selectable={false}
					handleCollumnTitleClick={(collumnIndex) =>
						setCollumnClicked(collumnIndex)
					}
					collumsObjIndex={[
						{
							collumnLabel: t("common.name"),
							index: "name",
							type: "custom",
							custom: ({ row }) => {
								const event = getEventData(row.name);
								return (
									<span className="flex flex-row items-center gap-[6px]!">
										{event?.icon ? React.createElement(event.icon, { size: 16, className: 'opacity-40!' }) : null}
										<span className="font-semibold!">{t(event?.name || row.name)}</span>
									</span>
								);
							}
						},
						{
							collumnLabel: t("common.type"), index: "type", type: "custom",
							custom: ({ row }) => {
								return (
									<span className="font-semibold!">{t(getEventTypeName(row.type))}</span>
								);
							}
						},
						{ collumnLabel: t("common.access"), index: "target_name", type: "text" },
						{
							collumnLabel: t("common.details"),
							index: "detail",
							type: "custom",
							custom: ({ row }) => {
								if (!row.detail || row.detail.trim() === '') {
									return (
										<span className="opacity-40">{t("common.doesNotContain")}</span>
									);
								}

								if (row.type === 'Acesso' && row.detail.startsWith('Lado')) {
									if (row.detail.endsWith('interno')) {
										return (
											<span className="text-(--exitColor) font-semibold! inline-flex items-center gap-1.5!">
												<TbDoorExit size={15} />
												{t("common.exit")}
											</span>
										);
									} else if (row.detail.endsWith('externo')) {
										return (
											<span className="text-(--entryColor) font-semibold! inline-flex items-center gap-1.5!">
												<TbDoorEnter size={15} />
												{t("common.entry")}
											</span>
										);
									}
								}

								return (
									<span>{row.detail}</span>
								);
							}
						},
						{ collumnLabel: t("common.user"), index: "user", type: "text" },
						{
							collumnLabel: t("common.time"),
							index: "fulldate",
							type: "custom",
							custom: ({ row }) => {
								return (
									<DatetimeWithRelative datetime={row.fulldate} />
								);
							}
						},
						{
							collumnLabel: t("common.result"),
							index: "result",
							type: "custom",
							custom: ({ row }) => {
								if (!row.result || row.result.trim() === '') {
									return (
										<span className="opacity-40">{t("common.doesNotContain")}</span>
									);
								}

								const isAccessEvent = ['Acesso', 'Alarme'].includes(row.type);
								const allow = row?.result === 'Sucesso';

								if (isAccessEvent) {
									return (
										<AllowAccessBadge allow={allow} />
									);
								}

								return (
									<span>{t(`${row.result}`)}</span>
								);
							}
						},
					]}
					expandableContent={({ row, toggleExpanded }) => {
						return (
							<div className="events-details-spacing">
								<EventDetails
									title={t("common.eventDetails")}
									event={row}
									isEvent={true}
									close={() => toggleExpanded(row.id)}
								/>
							</div>
						);
					}}
					data={events?.map(event => {
						const updatedEvent = { ...event };
						if (event.name.includes('panico') || event.name.includes('violada')) {
							updatedEvent._listLineClassName_ = "list-line-warning";
						}
						return {
							...updatedEvent,
							_expandable_: true,
							_expandableId_: event.id,
							_expandableLabel_: t("common.details")
						};
					})}
				/>
			</div>
			<div className="card-visibility">
				{events &&
					events?.map((event, index) => (
						<Card
							key={index}
							data={event}
							icon={<DefineIcon tipoEvento={event.type} />}
							hideCardEditDeleteButtons={true}
						>
							<p className="card-content-title">{event.name}</p>
							<p className="card-content">
								Serial Number: {event.device_serial_number}
							</p>
							<p className="card-content">Tipo: {event.type}</p>
							<p className="card-content">Detalhe: {event.detail}</p>
							<p className="card-content">Dados: {event.data}</p>
						</Card>
					))}
			</div>
			<Pagination
				currentPage={currentPage}
				rowsPerPage={rowsPerPage}
				maxPage={maxPage}
				total={total}
				changePage={(page) =>
					setCurrentPage(page)
				}
				changeRowsPerPage={(rowsPerPage) =>
					setRowsPerPage(rowsPerPage)
				}
			/>
		</React.Fragment>
	);
};

export default ListEvents;
